import { React, useEffect, useState } from 'react';
import {
    flexRender, getCoreRowModel, useReactTable, getPaginationRowModel,
    getFilteredRowModel, getSortedRowModel
} from '@tanstack/react-table';
import {
    toast, Spinner, Pagination, Table, Thead, Tr, Th, Td, Tbody, EmptyState,
    FlightReverseIllustration, Button, FilterIcon, TrashIcon, Tooltip, Dialog,
    Checkbox, AddIcon, SortableIcon, HistoryIcon
} from '@atlas-design-system/react';
import { Filter } from '../utils/Filter';
import { EditableCell } from '../utils/EditableCell';
import { NewRecordRow } from '../utils/NewRecordRow';

function AtlasTable(props) {

    const [data, setData] = useState([]);
    const [recordsToDelete, setRrecordsToDelete] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [withFilters, setWithFilters] = useState(true);
    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editableRowIndex, setEditableRowIndex] = useState(null);
    const [cellInfo, setCellInfo] = useState([]);
    const [sorting, setSorting] = useState([]);
    //const [saveEditButtonStatus, setSaveEditButtonStatus] = useState(false);

    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 3
    });

    const pageSizeOptions = props.pageSizeOptions ?? [
        { label: '5', value: 5 },
        { label: '10', value: 10 },
        { label: '20', value: 20 },
        { label: '50', value: 50 },
        { label: '100', value: 100 }
    ]

    useEffect(() => {
        loadEntries(props.loadRequest());
    }, []);

    const loadEntries = (loadRequest) => {
        setIsLoading(true);
        loadRequest.then(jsonResp => {
            if (jsonResp)
                setData(jsonResp);
        }).finally(() => setIsLoading(false));
    }

    const removeRecords = (fetchRequest) => {
        var d = [...data];
        setIsLoading(true);
        var results = recordsToDelete.map(r => {
            return fetchRequest(r);
        });
        Promise.all(results).then((records) => {
            //select ids from records and filter out from data
            var recordKeysToDelete = records.map(r => r[props.recordId]);
            var newData = d.filter(a => !recordKeysToDelete.includes(a[props.recordId]));
            setData(newData);
            if (records.length === recordKeysToDelete.length)
                toast.success('Record(s) deleted successfully', { position: 'top-right', duration: 6000 });
            else
                toast.warning('Some records were not deleted. Pleae check console logs for errors.', { position: 'top-right', duration: 6000 });
        }).catch(e => console.log(e))
            .finally(() => {
                table.resetRowSelection();
                setRrecordsToDelete([]);
                setDeleteDialogOpen(false);
                setIsLoading(false);
            });
    }

    const updateRecord = (index) => {
        var isValid = table.options.meta.cellsValidationData.every(a => !a.error);
        if (!isValid) {
            toast.warning('Please fix errors before saving', { position: 'top-right', duration: 6000 })
            return;
        }
        const unsavedRecord = { ...table.options.meta.unsavedRecord };
        if (!unsavedRecord) {
            toast.info('No changes to save (unsavedRecord is null)', { position: 'top-right', duration: 6000 })
            return;
        }
        //update record in storage....
        setIsLoading(true);
        props.updateRequest(unsavedRecord).then((a) => {
            if (a) {
                var d = [...data];
                d.splice(index, 1, unsavedRecord);
                setData(d);
            }
        }).catch(e => console.log(e))
            .finally(() => {
                setIsLoading(false);
                setEditableRowIndex(null);
            });
    }

    function validateCell(column, currentCellRecord, source) {
        var v = currentCellRecord.value;
        if (column.columnDef.required && !(typeof v === 'string' && v.trim().length > 0)) {
            currentCellRecord.error = "This field is required";
        } else if (column.columnDef.regEx && !(!column.columnDef.regEx || column.columnDef.regEx.test(v))) {
            currentCellRecord.error = column.columnDef.regExMessage;
        } else {
            currentCellRecord.error = '';
        }
        currentCellRecord.isValid = !currentCellRecord.error;
        //update source
        source.splice(source.findIndex(r => r.id === column.id), 1, currentCellRecord);
    }

    const cellValueChanged = (column, currentRecord, v) => {
        currentRecord.value = v;
        var source = [...cellInfo];
        validateCell(column, currentRecord, source);
        setCellInfo(source);
    }

    const addNewRecord = (columns) => {
        if (!cellInfo.every(r => r.isValid)) {
            toast.warning('Please fix errors before saving', { position: 'top-right', duration: 6000 })
            var cloneRecords = [...cellInfo];
            columns.forEach(column => {
                if (column.columnDef.editable) {
                    var currentCellRecord = { ...cloneRecords.find(r => r.id === column.id) };
                    validateCell(column, currentCellRecord, cloneRecords);
                }
            });
            setCellInfo(cloneRecords);
            return;
        }
        setIsLoading(true);
        var newRec = [...cellInfo].reduce((acc, r) => ({ ...acc, [r.id]: r.value }), {})
        props.addRequest(newRec)
            .then(a => {
                if (a) {
                    setData([a, ...data]);
                }
            }).catch(e => console.log(e))
            .finally(() => {
                setIsLoading(false);
                setEditing(false);
            });
    }

    const table = useReactTable({
        data: data,
        columns: props.columns,
        defaultColumn: {
            cell: (x) => EditableCell({ ...x, recordId: props.recordId })
        },
        state: { pagination, sorting },
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        getPaginationRowModel: getPaginationRowModel(),
        setEditableRowIndex,
        editableRowIndex,
        // Provide our updateUnsavedData function to our table meta
        meta: {
            unsavedRecord: null,
            removeRecord: (record) => {
                setRrecordsToDelete([record]);
                setDeleteDialogOpen(true);
            },
            updateRecord: updateRecord,
            cellsValidationData: []
        }
        // setSaveEditButtonStatus,
        // saveEditButtonStatus
    });

    return (<>
        <Dialog id='delete-confirmation'
            hidden={!isDeleteDialogOpen}
            title='Remove confirmation'
            appearance='warning'
            onDismiss={() => {
                setDeleteDialogOpen(false);
                setRrecordsToDelete([]);
            }}
            onConfirm={() => {
                setDeleteDialogOpen(false);
                removeRecords(props.deleteRequest);
            }}>
            Are you sure you want to remove {recordsToDelete.length > 1 ? 'ALL selected records' : 'this record'}?
        </Dialog>
        <div style={{ marginLeft: 'auto', display: 'table', marginRight: 0 }}>
            {!editing &&
                <Tooltip content='Add new record' delayShow={500}>
                    <Button appearance='tertiary' icon={<AddIcon width='20' />}
                        style={{ marginRight: 5 }}
                        onClick={() => {
                            setEditing(true);
                            table.resetRowSelection();
                            setEditableRowIndex(null);
                            setCellInfo(table.getAllColumns().reduce((acc, col) => {
                                if (col.id !== 'select' && col.id !== 'actions')
                                    acc.push({ value: '', id: col.id, isValid: !col.columnDef.required, error: '' });
                                return acc;
                            }, []));
                        }} />
                </Tooltip>
            }
            <Tooltip content='Show/Hide filters' delayShow={500}>
                <Button appearance='tertiary' icon={<FilterIcon width='20' />}
                    onClick={() => setWithFilters(x => !x)} />
            </Tooltip>
            {table.getSelectedRowModel().rows.length > 0 && (
                <Tooltip content='Remove selected' >
                    <Button appearance='tertiary' icon={<TrashIcon width='20' />}
                        delayShow={500}
                        onClick={() => {
                            setRrecordsToDelete([...table.getSelectedRowModel().rows].map(r => (r.original)));
                            setDeleteDialogOpen(true);
                        }}
                        style={{ marginLeft: 5 }} />
                </Tooltip>)}

            <Tooltip content='Reload' delayShow={500}>
                <Button appearance='tertiary' icon={<HistoryIcon width='20' />}
                    onClick={() => loadEntries(props.loadRequest())} />
            </Tooltip>
        </div>
        <Table className='editTable' next>
            <Thead>
                {table.getHeaderGroups().map((headerGroup) => (
                    <Tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                            if (editing && header.id === 'select') {
                                return (<Th key={header.id} isSelectable={header.id === 'select'}><Checkbox label='' disabled /></Th>);
                            }
                            return (
                                <Th
                                    key={header.id}
                                    align={header.column.columnDef.meta?.align}
                                    isSelectable={header.id === 'select'}
                                    isSortable={header.column.getCanSort()}
                                    aria-sort={
                                        header.column.getIsSorted()
                                            ? `${header.column.getIsSorted()}ending`
                                            : 'none'
                                    }
                                ><>
                                        {header.isPlaceholder ? null : (
                                            <div
                                                className={
                                                    header.column.getCanSort()
                                                        ? 'cursor-pointer select-none'
                                                        : ''
                                                }
                                                role='button'
                                                onClick={header.column.getToggleSortingHandler()}
                                                // onKeyDown={(event) => {
                                                //   onEnter(header.column.getToggleSortingHandler())(event);
                                                // }}
                                                tabIndex={0}
                                            >
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                                {header.column.getCanSort() &&
                                                    !header.column.getIsSorted() && <SortableIcon />}
                                                {{
                                                    asc: <SortableIcon direction='asc' />,
                                                    desc: <SortableIcon direction='desc' />
                                                }[header.column.getIsSorted()] ?? null}
                                            </div>

                                        )}
                                        {withFilters && header.column.getCanFilter() ? (
                                            <div style={{ background: 'white' }}>
                                                <Filter column={header.column} table={table} />
                                            </div>
                                        ) : null}
                                    </>
                                </Th>
                            );
                        })}
                    </Tr>
                ))}
            </Thead>
            <Tbody>
                {isLoading && (
                    <Tr>
                        <Td isLoading={isLoading}>
                            <Spinner
                                className='atls-table-spinner'
                                size='large'
                                percent={35}
                                primaryLabel='Loading data...'
                                secondaryLabel="This shouldn't take long."
                            />
                        </Td>
                    </Tr>
                )}
                {editing && cellInfo.length > 0 ? <NewRecordRow table={table} cellInfo={cellInfo}
                    cellValueChanged={cellValueChanged} addNewRecord={addNewRecord} setEditing={setEditing} />
                    : null
                }
                {table.getRowModel().rows.map((row) => (
                    <Tr key={row.id} className={row.getIsSelected() ? 'checked' : ''}>
                        {row.getVisibleCells().map((cell) => {
                            if (editing && (cell.column.columnDef.accessorKey === 'actions' || cell.column.columnDef.accessorKey === 'select')) {
                                return <Td key={cell.id} />
                            }
                            return (
                                <Td key={cell.id} align={cell.column.columnDef.meta?.align}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </Td>
                            );
                        })}
                    </Tr>
                ))}
            </Tbody>
        </Table>
        {table.getRowModel().rows.length > 0 ? (<div className='atls-table-with-pagination'>
            <Pagination
                className='sbPaginationExample'
                pageSizeOptions={pageSizeOptions}
                defaultPageSize={table.getState().pagination.pageSize}
                dataSize={table.getCoreRowModel().rows.length}
                pageIndex={table.getState().pagination.pageIndex}
                canNextPage={table.getCanNextPage()}
                canPreviousPage={table.getCanPreviousPage()}
                menuPlacement='auto'
                nextPage={table.nextPage}
                previousPage={table.previousPage}
                pageSize={table.getState().pagination.pageSize}
                onShowRowsChange={table.setPageSize}
            />
        </div>) : <EmptyState style={{ marginTop: 10 }}
            title='No records found'
            illustration={<FlightReverseIllustration />}
        />}
    </>);
}

export default AtlasTable;